import { useCallback, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Trash2, Loader2, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import { usePermissions } from '../hooks/usePermissions';

const STATUS_COLORS: Record<string, string> = {
  uploaded: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-yellow-100 text-yellow-800',
  extracted: 'bg-blue-100 text-blue-800',
  in_review: 'bg-orange-100 text-orange-800',
  verified: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = usePermissions();
  const canWrite = can('snapshots:write');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [conflictDocId, setConflictDocId] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<string>('tax_return');

  const { data, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.getDocuments(),
    refetchInterval: (query) => {
      const docs = query.state.data?.documents ?? [];
      const hasProcessing = docs.some((d: any) =>
        d.processingStatus === 'uploaded' || d.processingStatus === 'processing'
      );
      return hasProcessing ? 3000 : false;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, docType, replaceId }: { file: File; docType: string; replaceId?: string }) =>
      api.uploadDocument(file, docType, docType === 'tax_return' ? new Date().getFullYear() - 1 : undefined, undefined, replaceId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['profileCompleteness'] });
      queryClient.invalidateQueries({ queryKey: ['nextActions'] });
      toast.success('Document uploaded. Processing...', {
        label: 'View',
        onClick: () => navigate(`/documents/${data.id}`),
      });
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        setConflictDocId(error.response.data.existingDocumentId);
        return;
      }
      toast.error(error.response?.data?.message ?? 'Upload failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Document deleted');
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryDocument(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(data.message ?? 'Retry started');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? 'Retry failed');
    },
  });

  const handleFiles = useCallback((files: FileList | null) => {
    if (!canWrite || !files || files.length === 0) return;
    const file = files[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid file type. Please upload a PDF, PNG, or JPG.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20MB.');
      return;
    }
    setPendingFile(file);
    uploadMutation.mutate({ file, docType: selectedDocType });
  }, [uploadMutation, toast]);

  const documents = data?.documents ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
        <p className="text-gray-600">Upload financial documents to build your wealth profile</p>
      </div>

      {/* Document Type Selector + Upload Zone */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Document type:</label>
          <select
            value={selectedDocType}
            onChange={(e) => setSelectedDocType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="tax_return">Tax Return</option>
            <option value="insurance_policy">Insurance Policy</option>
            <option value="estate_document">Estate Document</option>
            <option value="brokerage_statement">Account Statement</option>
          </select>
        </div>
        <div
          className={clsx(
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
            dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400',
            uploadMutation.isPending && 'opacity-50 pointer-events-none'
          )}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {uploadMutation.isPending ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm font-medium text-gray-700">
                {selectedDocType === 'insurance_policy'
                  ? 'Drop your insurance policy here, or click to browse'
                  : selectedDocType === 'estate_document'
                  ? 'Drop your estate document here, or click to browse'
                  : selectedDocType === 'brokerage_statement'
                  ? 'Drop your account statement here, or click to browse'
                  : 'Drop your tax return here, or click to browse'}
              </p>
              <p className="mt-1 text-xs text-gray-500">PDF, PNG, or JPG up to 20MB</p>
            </div>
          )}
        </div>
      </div>

      {/* Document List */}
      {documents.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Upload your most recent tax return to begin your wealth analysis."
        />
      ) : (
        <div className="bg-white rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc: any) => (
                <tr
                  key={doc.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/documents/${doc.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{doc.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{doc.taxYear ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[doc.processingStatus] ?? 'bg-gray-100 text-gray-800')}>
                      {doc.processingStatus === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      {doc.processingStatus === 'verified' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {doc.processingStatus === 'failed' && <AlertCircle className="h-3 w-3 mr-1" />}
                      {doc.processingStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {doc.factCounts ? `${doc.factCounts.confirmed + doc.factCounts.corrected}/${doc.factCounts.total} verified` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                    {doc.processingStatus === 'failed' && (
                      <button
                        className="text-gray-400 hover:text-blue-500"
                        title="Retry extraction"
                        onClick={(e) => { e.stopPropagation(); retryMutation.mutate(doc.id); }}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      className="text-gray-400 hover:text-red-500"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(doc.id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Document"
        description="This will permanently delete this document and all extracted data. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget); setDeleteTarget(null); }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Conflict / replace confirmation */}
      <ConfirmDialog
        open={!!conflictDocId}
        title="Document Already Exists"
        description="A tax return for this year already exists. Do you want to replace it?"
        confirmLabel="Replace"
        onConfirm={() => {
          if (pendingFile && conflictDocId) {
            uploadMutation.mutate({ file: pendingFile, docType: selectedDocType, replaceId: conflictDocId });
          }
          setConflictDocId(null);
        }}
        onCancel={() => { setConflictDocId(null); setPendingFile(null); }}
      />
    </div>
  );
}
